// ✅ ALTERED ONLY FOR THEME (matches your “above theme”)
// - Orange header bar + white text
// - Page bg #F2F2F2 + white inner surface with subtle blue border
// - Left orange accent stripe section
// - Inputs with soft blue border + orange focus
// - Buttons: primary orange, outlined blue
// - QR + info cards also styled to match
// - No business logic changes

import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Snackbar,
  Alert,
  Divider,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import { QRCodeCanvas } from "qrcode.react";
import { API_URL } from "./config";

const AdminGuardPairing = () => {
  const token = localStorage.getItem("auth");

  // ✅ Theme constants (same palette)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";

  // ✅ shared sx
  const pageWrapSx = { p: 2, backgroundColor: BG };
  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };
  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
  };
  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const miniCardSx = {
    ...surfaceSx,
    p: 2,
  };

  const [deviceLabel, setDeviceLabel] = useState("Main Gate");
  const [pairingUrl, setPairingUrl] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [expiresInMinutes, setExpiresInMinutes] = useState(null);

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });

  const showToast = (msg, sev = "success") => setToast({ open: true, msg, sev });

  const generatePairing = async () => {
    if (!token) {
      showToast("Login required", "error");
      return;
    }

    setLoading(true);
    try {
      const resp = await axios.post(
        `${API_URL}/api/guard/pairing/create`,
        { deviceLabel },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setPairingUrl(resp.data.pairingUrl || "");
      setPairingCode(resp.data.pairingCode || "");
      setExpiresInMinutes(resp.data.expiresInMinutes ?? null);

      showToast("Pairing QR generated", "success");
    } catch (e) {
      console.error(e);
      showToast(e.response?.data?.message || "Failed to generate pairing QR", "error");
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pairingUrl);
      showToast("Copied pairing link", "success");
    } catch {
      showToast("Copy failed", "error");
    }
  };

  const reset = () => {
    setPairingUrl("");
    setPairingCode("");
    setExpiresInMinutes(null);
  };

  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Pair Guard Device (Passkey)
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Generate a one-time QR for guard device passkey registration
            </Typography>
          </Box>
        </Box>

        <Box sx={bodySx}>
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Pairing Setup
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              <Typography sx={{ mb: 2, color: "rgba(0,0,0,0.75)" }}>
                Generate a one-time QR for the guard device. The guard opens the link and registers a passkey.
              </Typography>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Device Label"
                  value={deviceLabel}
                  onChange={(e) => setDeviceLabel(e.target.value)}
                  fullWidth
                  sx={inputSx}
                />

                <Button
                  variant="contained"
                  onClick={generatePairing}
                  disabled={loading}
                  startIcon={<RefreshIcon />}
                  sx={{ ...primaryBtnSx, minWidth: 240, alignSelf: { xs: "stretch", md: "auto" } }}
                >
                  {loading ? "Generating..." : "Generate Pairing QR"}
                </Button>

                <Button
                  variant="outlined"
                  onClick={reset}
                  disabled={!pairingUrl}
                  sx={{ ...outlinedBtnSx, minWidth: 140, alignSelf: { xs: "stretch", md: "auto" } }}
                >
                  Clear
                </Button>
              </Stack>

              {pairingUrl && (
                <Box sx={{ mt: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {/* QR Card */}
                  <Paper sx={{ ...miniCardSx, textAlign: "center", minWidth: 300 }}>
                    <Box
                      sx={{
                        display: "inline-flex",
                        p: 1.25,
                        borderRadius: 2,
                        border: `1px dashed ${BORDER}`,
                        backgroundColor: "rgba(25,118,210,0.03)",
                      }}
                    >
                      <QRCodeCanvas value={pairingUrl} size={260} />
                    </Box>

                    <Typography sx={{ mt: 1, fontWeight: 900, color: "rgba(0,0,0,0.78)" }}>
                      Scan on Guard Device
                    </Typography>

                    {expiresInMinutes != null && (
                      <Typography sx={{ mt: 0.5, fontWeight: 800, color: THEME_ORANGE }}>
                        Expires in {expiresInMinutes} minutes (one-time use)
                      </Typography>
                    )}
                  </Paper>

                  {/* Info Card */}
                  <Paper sx={{ ...miniCardSx, flex: 1, minWidth: 320 }}>
                    <Typography sx={{ fontWeight: 900, color: THEME_ORANGE }}>
                      Pairing Link
                    </Typography>
                    <Divider sx={{ mt: 1, mb: 1.5, borderColor: "rgba(25,118,210,0.12)" }} />

                    <Typography sx={{ wordBreak: "break-all", color: "rgba(0,0,0,0.75)" }}>
                      {pairingUrl}
                    </Typography>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
                      <Button
                        variant="contained"
                        startIcon={<ContentCopyIcon />}
                        onClick={copyLink}
                        sx={primaryBtnSx}
                      >
                        Copy Link
                      </Button>
                    </Stack>

                    <Typography sx={{ mt: 2, fontWeight: 900, color: THEME_ORANGE }}>
                      Pairing Code
                    </Typography>
                    <Divider sx={{ mt: 1, mb: 1.5, borderColor: "rgba(25,118,210,0.12)" }} />

                    <Typography sx={{ wordBreak: "break-all", fontWeight: 800 }}>
                      {pairingCode}
                    </Typography>

                    <Alert
                      severity="info"
                      sx={{
                        mt: 2,
                        borderRadius: 2,
                        border: "1px solid rgba(25,118,210,0.18)",
                        backgroundColor: "rgba(25,118,210,0.04)",
                      }}
                    >
                      On the guard phone, open the link and tap <b>Register Passkey</b>.
                    </Alert>
                  </Paper>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Paper>

      <Snackbar open={toast.open} autoHideDuration={3000} onClose={closeToast}>
        <Alert severity={toast.sev} onClose={closeToast} sx={{ width: "100%" }}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminGuardPairing;
